import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faTable } from "@fortawesome/pro-regular-svg-icons";
import { capitalize } from "lodash-es";

import { Schema, SchemaTypesBE, SubSchema } from "src/api/flowTypes";
import type { TemplateWithVersion } from "src/cases/queries/templates";
import { getTemplateIcon } from "src/cases/templates/utils";
import {
  CaseTemplateJsonSchemaAttributes,
  CaseTemplateJsonSchemaResponse,
  CaseTemplateVersionPublishState,
} from "src/clients/case-management";
import { unslugify } from "src/utils/pythonUtils";

const PRIMITIVE_TYPES: string[] = [
  SchemaTypesBE.string,
  SchemaTypesBE.number,
  SchemaTypesBE.integer,
  SchemaTypesBE.boolean,
];
const REF_PREFIX = "#/$defs/";
const CASE_PROPERTY_NAME = "case";
const DATA_CASE_PATH = "data.case";

export type JsonSchemaProperty = {
  type?: string;
  title?: string;
  description?: string;
  format?: string;
  enum?: string[];
  properties?: Record<string, JsonSchemaProperty>;
  items?: JsonSchemaProperty;
  required?: string[];
  $ref?: string;
  oneOf?: JsonSchemaProperty[];
  anyOf?: JsonSchemaProperty[];
  additionalProperties?: boolean | JsonSchemaProperty;
  default?: unknown;
  $defs?: Record<string, JsonSchemaProperty>;
};

export type TemplateMetadata = {
  templateId: string;
  versionId: string;
  schemaVersion?: number;
  publishState?: CaseTemplateVersionPublishState;
  etag?: string;
  templateName?: string;
  versionName?: string;
};

export type ParsedSchemaSection = {
  id: string;
  name: string;
  path: string;
  properties: Record<string, JsonSchemaProperty>;
  originalProperty: JsonSchemaProperty;
};

export type CaseSchemaDisplay = {
  id: string;
  name: string;
  icon: IconProp;
  primitiveProperties: Record<string, JsonSchemaProperty>;
  nestedSections: ParsedSchemaSection[];
};

export type SelectedTemplateInfo = {
  templateId: string;
  versionId: string;
  templateName: string;
  versionName: string;
  icon: IconProp;
};

const isJsonSchemaProperty = (value: unknown): value is JsonSchemaProperty => {
  return typeof value === "object" && value !== null && !Array.isArray(value);
};

const hasProperties = (
  object: unknown,
): object is { properties: Record<string, unknown> } => {
  return (
    isJsonSchemaProperty(object) &&
    "properties" in object &&
    typeof object.properties === "object"
  );
};

const hasCaseProperty = (
  object: unknown,
): object is {
  properties: Record<string, unknown> & { case: JsonSchemaProperty };
  $defs?: Record<string, unknown>;
} => {
  return hasProperties(object) && CASE_PROPERTY_NAME in object.properties;
};

const isSimpleUnion = (union: JsonSchemaProperty[]): boolean => {
  if (union.length === 1) {
    const type = union[0]?.type;
    return type !== undefined && PRIMITIVE_TYPES.includes(type);
  }

  if (union.length === 2) {
    const nonNullOptions = union.filter((option) => option?.type !== "null");
    if (nonNullOptions.length === 1) {
      const type = nonNullOptions[0]?.type;
      return type !== undefined && PRIMITIVE_TYPES.includes(type);
    }
  }

  return false;
};

export const isPrimitiveProperty = (property: JsonSchemaProperty): boolean => {
  if (property.anyOf && isSimpleUnion(property.anyOf)) {
    return true;
  }

  if (property.oneOf && isSimpleUnion(property.oneOf)) {
    return true;
  }

  const hasComplexFeatures = !!(
    property.$ref ||
    property.properties ||
    property.items ||
    property.additionalProperties ||
    (property.anyOf && !isSimpleUnion(property.anyOf)) ||
    (property.oneOf && !isSimpleUnion(property.oneOf))
  );

  if (hasComplexFeatures) {
    return false;
  }

  return property.type !== undefined && PRIMITIVE_TYPES.includes(property.type);
};

export const formatSectionTitle = (
  property: JsonSchemaProperty,
  key: string,
): string => {
  const title =
    property.title && !property.title.includes("_")
      ? property.title
      : unslugify(key);

  return capitalize(title);
};

const findTemplateBySelection = (
  templatesData: TemplateWithVersion[] | undefined,
  selection: SelectedTemplateInfo,
): TemplateWithVersion | undefined => {
  return templatesData?.find(
    (template) =>
      template.templateId === selection.templateId &&
      template.versionId === selection.versionId,
  );
};

export const resolveRefsInline = <T extends Record<string, unknown>>(
  schema: T,
  defs: Record<string, SubSchema> | undefined,
): T => {
  if (!defs) return schema;

  const resolveValue = (value: unknown): unknown => {
    if (Array.isArray(value)) {
      return value.map(resolveValue);
    }

    if (value && typeof value === "object") {
      const obj = value as Record<string, unknown>;

      if (obj.$ref && typeof obj.$ref === "string") {
        const refKey = obj.$ref.replace(REF_PREFIX, "");
        const definition = defs[refKey];
        if (definition) {
          return resolveValue(definition);
        }
      }

      return Object.fromEntries(
        Object.entries(obj).map(([key, val]) => [key, resolveValue(val)]),
      );
    }

    return value;
  };

  return resolveValue(schema) as T;
};

const extractSchemaAndDefs = (
  caseJsonSchema: CaseTemplateJsonSchemaAttributes["case_json_schema"],
) => {
  if (hasCaseProperty(caseJsonSchema)) {
    const caseProperty = caseJsonSchema.properties[CASE_PROPERTY_NAME];
    return {
      schema: isJsonSchemaProperty(caseProperty) ? caseProperty : {},
      rootDefs: caseJsonSchema.$defs,
    };
  }

  if (isJsonSchemaProperty(caseJsonSchema)) {
    return {
      schema: caseJsonSchema,
      rootDefs: caseJsonSchema.$defs,
    };
  }

  throw new Error("Invalid case JSON schema structure");
};

export const transformTemplateToFlowSchema = (
  caseJsonSchema: CaseTemplateJsonSchemaAttributes["case_json_schema"],
  metadata: TemplateMetadata,
): Schema => {
  const { schema, rootDefs } = extractSchemaAndDefs(caseJsonSchema);

  if (!schema || typeof schema !== "object") {
    throw new Error("Invalid schema structure");
  }

  const { $defs: caseDefs = {}, ...schemaWithoutDefs } = schema;

  const allDefs = {
    ...rootDefs,
    ...caseDefs,
  } as Record<string, SubSchema>;

  const resolvedSchema = resolveRefsInline(schemaWithoutDefs, allDefs);

  return {
    $schema: "https://json-schema.org/draft/2020-12/schema",
    type: "object",
    properties: {
      [CASE_PROPERTY_NAME]: {
        ...resolvedSchema,
        _template_metadata: metadata,
      } as Record<string, unknown>,
    },
    required: [CASE_PROPERTY_NAME],
    sensitive: [],
    order: [CASE_PROPERTY_NAME],
  };
};

export const transformTemplateDataSchemaToFlowSchema = (
  attributes: CaseTemplateJsonSchemaAttributes,
  metadata: Omit<TemplateMetadata, "schemaVersion" | "publishState">,
): Schema =>
  transformTemplateToFlowSchema(attributes.case_json_schema, {
    ...metadata,
    ...(attributes.schema_version != null && {
      schemaVersion: attributes.schema_version,
    }),
    ...(attributes.publish_state != null && {
      publishState: attributes.publish_state,
    }),
  });

type CaseJsonSchema = {
  $defs?: Record<string, SubSchema>;
  properties: Record<string, JsonSchemaProperty>;
};

const extractCaseProperties = (
  caseJsonSchema: CaseJsonSchema,
): Record<string, JsonSchemaProperty> => {
  const defs = caseJsonSchema.$defs;

  if (hasCaseProperty(caseJsonSchema)) {
    const caseProperty = caseJsonSchema.properties[CASE_PROPERTY_NAME];
    if (isJsonSchemaProperty(caseProperty) && caseProperty.properties) {
      const resolvedProperties = resolveRefsInline(
        caseProperty.properties,
        defs,
      );
      return resolvedProperties;
    }
  }

  if (
    caseJsonSchema.properties &&
    typeof caseJsonSchema.properties === "object"
  ) {
    const properties: Record<string, JsonSchemaProperty> = {};

    for (const [key, property] of Object.entries(caseJsonSchema.properties)) {
      if (isJsonSchemaProperty(property)) {
        properties[key] = property;
      }
    }

    const resolvedProperties = resolveRefsInline(properties, defs);
    return resolvedProperties;
  }

  return {};
};

const extractNestedProperties = (
  property: JsonSchemaProperty,
): Record<string, JsonSchemaProperty> => {
  const allProperties: Record<string, JsonSchemaProperty> = {};

  const addProperties = (props: Record<string, JsonSchemaProperty>) => {
    Object.entries(props).forEach(([key, prop]) => {
      // Filter out additional_inputs at all nesting levels
      if (key === "additional_inputs") {
        return;
      }
      allProperties[key] = prop;
    });
  };

  const processSchema = (schema: JsonSchemaProperty) => {
    if (schema.properties) {
      addProperties(schema.properties);
    }

    if (schema.type === "array" && schema.items) {
      const items = schema.items;
      if (items && typeof items === "object") {
        processSchema(items);
      }
    }

    if (schema.oneOf) {
      schema.oneOf.forEach((option) => {
        if (option && typeof option === "object") {
          processSchema(option);
        }
      });
    }

    if (schema.anyOf) {
      schema.anyOf.forEach((option) => {
        if (option && typeof option === "object") {
          processSchema(option);
        }
      });
    }

    if (
      schema.additionalProperties &&
      typeof schema.additionalProperties === "object"
    ) {
      const additionalProps = schema.additionalProperties;
      processSchema(additionalProps);
    }
  };

  processSchema(property);
  return allProperties;
};

const processSchemaProperties = (
  properties: Record<string, JsonSchemaProperty>,
) => {
  const primitiveProperties: Record<string, JsonSchemaProperty> = {};
  const nestedSections: ParsedSchemaSection[] = [];

  Object.entries(properties).forEach(([key, property]) => {
    if (key === "additional_inputs") {
      return;
    }
    if (isPrimitiveProperty(property)) {
      primitiveProperties[key] = property;
    } else {
      const nestedProperties = extractNestedProperties(property);
      nestedSections.push({
        id: key,
        name: formatSectionTitle(property, key),
        path: `${DATA_CASE_PATH}.${key}`,
        properties: nestedProperties,
        originalProperty: property,
      });
    }
  });

  return { primitiveProperties, nestedSections };
};

const buildCaseSchemaDisplay = (
  properties: Record<string, JsonSchemaProperty>,
  template: TemplateWithVersion,
): CaseSchemaDisplay => {
  const { primitiveProperties, nestedSections } =
    processSchemaProperties(properties);

  return {
    id: template.templateId,
    name: template.templateName,
    icon: getTemplateIcon(template.icon) ?? faTable,
    primitiveProperties,
    nestedSections,
  };
};

export const createSelectedSchema = (
  templateSchemaResponse: CaseTemplateJsonSchemaResponse | undefined,
  templatesData: TemplateWithVersion[] | undefined,
  selectedTemplate: SelectedTemplateInfo | null,
  currentSchema: Schema | undefined,
): CaseSchemaDisplay | undefined => {
  if (templateSchemaResponse?.data && selectedTemplate) {
    const caseJsonSchema =
      templateSchemaResponse.data.attributes.case_json_schema;

    const templateData = findTemplateBySelection(
      templatesData,
      selectedTemplate,
    );

    if (templateData && hasProperties(caseJsonSchema)) {
      const properties = extractCaseProperties(
        caseJsonSchema as CaseJsonSchema,
      );
      return buildCaseSchemaDisplay(properties, templateData);
    }
  }

  if (selectedTemplate && currentSchema?.properties?.case) {
    const caseProperty = currentSchema.properties.case;
    const caseProperties = isJsonSchemaProperty(caseProperty)
      ? caseProperty.properties || {}
      : {};

    const { primitiveProperties, nestedSections } =
      processSchemaProperties(caseProperties);

    return {
      id: selectedTemplate.templateId,
      name: selectedTemplate.templateName,
      icon: selectedTemplate.icon,
      primitiveProperties,
      nestedSections,
    };
  }

  return undefined;
};
